import { STORAGE_FOLDERS, uploadImage } from '@/firebase';
import { formatUTCDate, createDownloadableLink } from '@/helpers';
import { FORMAT_DD_MM_YYYY } from '@/module/company/constants';
import moment from 'moment';
import { synthesize } from 'redux-dispatcher';

const mapDispatchToAC = {
  getDataSuccess: (data) => ({ data }),

  getData:
    (search, paging, filterOption = {}, sortOption = {}, patientId) =>
    async ({ Api, getState }) => {
      let pagingData = paging || getState().teleAppoinment.paging;

      let { result, status } = await Api.post(`patient-appointments/filter`, {
        pageIndex: pagingData.pageIndex,
        pageSize: pagingData.pageSize,
        search,
        byHealthScreening: !patientId,
        ...filterOption,
        startDate: filterOption?.startDate
          ? formatUTCDate(filterOption?.startDate, { isStarOfDay: true })
          : undefined,
        endDate: filterOption?.endDate
          ? formatUTCDate(filterOption?.endDate, { isEndOfDay: true })
          : undefined,
        ...sortOption,
        patientId,
      });

      status === 200 && teleAppoinmentDispatcher.getDataSuccess(result);
    },
  getDataByPatientId:
    (search, paging, sortOption = {}, filterOption = {}, patientId) =>
    async ({ Api, getState }) => {
      let pagingData = paging || getState().teleAppoinment.paging;

      let { result, status } = await Api.post(
        `patient-appointments/${patientId}`,
        {
          createdEndDate: filterOption?.createdEndDate,
          createdStartDate: filterOption?.createdStartDate,
          appointmentStatuses: filterOption?.appointmentStatuses,
          paxScreenPaymentStatus: filterOption?.paxScreenPaymentStatus,
          isFreePayment: filterOption?.isFreePayment,
          locationIds: filterOption?.locationIds,
          fromDate: filterOption?.startDate
            ? formatUTCDate(filterOption?.startDate, { isStarOfDay: true })
            : undefined,
          toDate: filterOption?.endDate
            ? formatUTCDate(filterOption?.endDate, { isEndOfDay: true })
            : undefined,
          types: filterOption?.appointmentTypes,
          pageIndex: pagingData.pageIndex,
          pageSize: pagingData.pageSize,
          search,
        }
      );

      status === 200 && teleAppoinmentDispatcher.getDataSuccess(result);
    },

  resetData: () => ({}),
  getConsentsByPatientID:
    (id, callback) =>
    async ({ Api }) => {
      const { result, status } = await Api.get(
        `pax-consents/by-pax-screening/${id}`
      );
      status === 200 && callback && callback(result);
    },
  getConsentsByServices:
    (data, callback) =>
    async ({ Api }) => {
      const { result, status } = await Api.post(
        `consents/get-test-consents`,
        data
      );
      status === 200 && callback && callback(result);
    },
  cancelTeleAppointment:
    (id, callback) =>
    async ({ Api }) => {
      const { status } = await Api.put(`pax-screenings/${id}/cancellation`);
      status === 200 && callback && callback();
    },
  patientCheckIn:
    (id, data, callback) =>
    async ({ Api, customToast }) => {
      const newData = { ...data };

      let dataLstTests = [];
      data.patientData.lstTests.forEach((it) => {
        if (it.total) {
          dataLstTests = dataLstTests.concat(it.total);
        } else {
          dataLstTests = dataLstTests.concat(it);
        }
      });
      newData.patientData.lstTests = dataLstTests;
      const { status } = await Api.put(
        `pax-screenings/${id}/check-in`,
        newData
      );
      if (status === 200) {
        customToast('success', 'Success');
        callback && callback();
      }
    },
  setState: (state, value) => ({ state, value }),
  signConsentForm:
    (id, data, callback) =>
    async ({ Api, customToast }) => {
      teleAppoinmentDispatcher.setState('loading', true);

      let { staffSignatureUrl } = data;
      if (data.staffSignatureUrl?.constructor === File) {
        staffSignatureUrl = await uploadImage(
          data.staffSignatureUrl,
          undefined,
          STORAGE_FOLDERS.consentForm
        );
      }

      const { status } = await Api.put(`pax-consents/sign/${id}`, {
        ...data,
        staffSignatureUrl,
      });
      teleAppoinmentDispatcher.setState('loading', false);

      if (status === 200) {
        customToast('success', 'Success');
        callback && callback();
      }
    },
  getExportExcel:
    (params) =>
    async ({ Api }) => {
      let { result, status } = await Api.post(
        `patient-appointments/exports/csv`,
        {
          ...params,
          byHealthScreening: !params.userId,
          startDate: params?.startDate
            ? formatUTCDate(params?.startDate, { isStarOfDay: true })
            : undefined,
          endDate: params?.endDate
            ? formatUTCDate(params?.endDate, { isEndOfDay: true })
            : undefined,
          createdStartDate: params?.createdStartDate
            ? formatUTCDate(params?.createdStartDate, { isStarOfDay: true })
            : undefined,
          createdEndDate: params?.createdEndDate
            ? formatUTCDate(params?.createdEndDate, { isEndOfDay: true })
            : undefined,
        }
      );

      status === 200 && createDownloadableLink(result, 'Result');
    },

  editTeleAppointment:
    (params, cb) =>
    async ({ Api }) => {
      const { id, timeSlotId, date, isSuppression, healthScreeningId } = params;
      let { status } = await Api.put(`patient-appointments/${id}/telescreen`, {
        timeSlotId,
        date: moment(date).format('YYYY-MM-DD'),
        isSuppression,
        healthScreeningId,
      });
      if (status === 200) {
        cb && cb({ isSuccess: true });
      } else {
        cb && cb({ isSuccess: false });
      }
    },
  downloadReportByStatus:
    (id, shortId) =>
    async ({ Api }) => {
      const response = await Api.post(
        `pax-screenings/${id}/export-profile-statement`,
        {}
      );

      const { status, result } = response;

      const fileName = `${shortId}/hs-appointment/${Date.now()}`;

      status === 200 && createDownloadableLink(result, fileName, 'pdf');
    },
  getPaxScreening:
    (id, onSuccess, onFail) =>
    async ({ Api }) => {
      const { status, result } = await Api.get(
        `analysis-pax-screenings/${id}/report`
      );
      status === 200 ? onSuccess(result) : onFail();
    },
  getPaxScreeningWithToken:
    (id, accessToken, onSuccess, onFail) =>
    async ({ Api }) => {
      if (accessToken) {
        const { status, result } = await Api.get(
          `analysis-pax-screenings/${id}/report`,
          undefined,
          { Authorization: `Bearer ${accessToken}` }
        );

        status === 200 ? onSuccess(result) : onFail();
      }
    },
  staffRegistersForPatient:
    (params, callback) =>
    async ({ Api, customToast }) => {
      const response = await Api.post(`pax-screenings/staff-register`, {
        ...params,
      });

      const { status } = response;
      if (status === 200) {
        customToast('success', 'Register successs');
        callback && callback({ isSuccess: true });
      } else {
        callback && callback({ isSuccess: false });
      }
    },
  patientCheckInLocation:
    (id, callback) =>
    async ({ Api, customToast }) => {
      const { status } = await Api.put(
        `pax-screenings/${id}/check-in-location`
      );
      if (status === 200) {
        customToast('success', 'Check-in location success');
        callback && callback();
      }
    },
  followUpAppointment:
    (id, params, callback) =>
    async ({ Api, customToast }) => {
      const response = await Api.post(`pax-screenings/${id}/follow-up-appt`, {
        ...params,
      });

      const { status } = response;
      if (status === 200) {
        customToast('success', 'Follow up successs');
        callback && callback({ isSuccess: true });
      } else {
        callback && callback({ isSuccess: false });
      }
    },
  downloadIRReport:
    (id, callback) =>
    async ({ Api, customToast }) => {
      const { status, result } = await Api.get(
        `analysis-pax-screenings/${id}/ir-report`
      );
      if (status === 200) {
        customToast('success', 'Download success');
        createDownloadableLink(result, 'Health Screening Report', 'pdf');
      }
    },
  getLinkDownloadIRReport:
    (id, callback) =>
    async ({ Api }) => {
      const { status, result } = await Api.get(
        `analysis-pax-screenings/${id}/ir-report`
      );
      if (status === 200) callback && callback(result);
    },
  generateIRReport:
    (id, token, paxScreening, callback) =>
    async ({ Api }) => {
      const jsonData = JSON.stringify({
        token,
        paxScreening: {
          id,
          ...paxScreening,
        },
      });
      const { status } = await Api.post(
        `analysis-pax-screenings/generate-ir-report`,
        {
          jsonData,
        }
      );
      if (status === 200) {
        callback && callback();
      }
    },
  generateLabNumber:
    (id, callback) =>
    async ({ Api, customToast }) => {
      const response = await Api.post(`pax-screenings/${id}/lab-number`);

      const { status } = response;
      if (status === 200) {
        customToast('success', 'Generate successs');
        callback && callback(true);
      }
    },
  exportInnoquestOrderList:
    (params) =>
    async ({ Api }) => {
      const { status, result } = await Api.post(
        `patient-appointments/innoquest-orders/exports/csv`,
        {
          ...params,
          healthScreeningDate: params?.healthScreeningDate
            ? formatUTCDate(params?.healthScreeningDate, { isStarOfDay: true })
            : undefined,
        }
      );
      const fileName = `Order_List_${moment(params.healthScreeningDate).format(
        FORMAT_DD_MM_YYYY
      )}`;
      status === 200 && createDownloadableLink(result, fileName, 'xlsx');
    },
  getDataInnoquestOrderList:
    (search, paging, filterOption = {}, sortOption = {}) =>
    async ({ Api, getState }) => {
      let pagingData = paging || getState().teleAppoinment.paging;

      let { result, status } = await Api.post(
        `patient-appointments/innoquest-orders/search`,
        {
          pageIndex: pagingData.pageIndex,
          pageSize: pagingData.pageSize,
          search,
          ...filterOption,
          date: filterOption?.date
            ? formatUTCDate(filterOption?.date, { isStarOfDay: true })
            : undefined,
          ...sortOption,
        }
      );

      status === 200 && teleAppoinmentDispatcher.getDataSuccess(result);
    },
  emailOrderListToLab:
    (params) =>
    async ({ Api, customToast }) => {
      const { status } = await Api.post(
        `patient-appointments/innoquest-orders/lab-emails`,
        {
          ...params,
          healthScreeningDate: params?.healthScreeningDate
            ? formatUTCDate(params?.healthScreeningDate, { isStarOfDay: true })
            : undefined,
        }
      );
      if (status === 200) {
        customToast('success', 'Email to lab successs');
      }
    },
  regenerateIRReport:
    (id) =>
    async ({ Api, customToast }) => {
      const { status } = await Api.put(
        `analysis-pax-screenings/${id}/ir-report/regenerations`
      );
      if (status === 200) {
        customToast('success', 'Regenerate success');
      }
    },
  revertAppoinmentToUpcoming:
    (id, callback) =>
    async ({ Api, customToast }) => {
      const { status } = await Api.put(`pax-screenings/${id}/revert`);
      if (status === 200) {
        customToast('success', 'Revert success');
        callback && callback();
      }
    },
  getAllPaxConsents:
    (data, callback) =>
    async ({ Api }) => {
      const { result, status } = await Api.post(`pax-consents/filter`, data);
      status === 200 && callback && callback(result);
    },
  createLabNumber:
    (id, params, callback) =>
    async ({ Api, customToast }) => {
      const { status, message } = await Api.post(
        `pax-screenings/${id}/input-lab-number`,
        params
      );

      if (status === 200) {
        customToast('success', 'Generate successs');
        callback && callback();
      }
      if (status === 400) {
        callback && callback(message);
      }
    },
  doneAllConsents:
    (id, callback) =>
    async ({ Api, customToast }) => {
      const { status } = await Api.put(
        `pax-screenings/${id}/done-all-consents`
      );
      if (status === 200) {
        customToast('success', 'Update all consents success');
        callback && callback();
      }
    },
};

const teleAppoinmentDispatcher = synthesize('teleAppoinment', mapDispatchToAC);

export default teleAppoinmentDispatcher;
